import { ref } from 'vue';
import { forgetAnalytics } from './analytics';

/**
 * The visitor's answer about analytics (lib/analytics.ts), on this device.
 *
 * `null` is "not asked yet", and the banner shows. Nothing non-essential
 * happens until the answer is 'granted'; 'denied' is remembered so the
 * question is not asked on every visit. Kept in localStorage: it is the
 * visitor's own setting, not the account's, and a signed-out visitor has one.
 * Versioned, so a change in what analytics does can ask again.
 */
const STORAGE_KEY = 'sulje.consent.analytics.v1';

export type Consent = 'granted' | 'denied';

function read(): Consent | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export const analyticsConsent = ref<Consent | null>(read());
/** The footer's "Cookie settings" reopens the banner without changing the answer. */
export const consentBannerOpen = ref(analyticsConsent.value === null);

export function setAnalyticsConsent(value: Consent): void {
  const before = analyticsConsent.value;
  analyticsConsent.value = value;
  consentBannerOpen.value = false;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Not remembered; the banner asks again next visit, which is the safe side.
  }
  if (before === 'granted' && value === 'denied') forgetAnalytics();
}
